import OrderService from "../orders/orderService";

/**
 * Estados posibles de un pedido
 */
export type OrderStatus =
  | "pending"
  | "paid"
  | "queued"
  | "preparing"
  | "ready"
  | "completed"
  | "cancelled";

/**
 * Interfaz para pedido en cola
 */
export interface QueuedOrder {
  orderId: string;
  customerName: string;
  items: Array<{
    id: string;
    name: string;
    quantity: number;
    price: number;
  }>;
  total: number;
  queuedAt: string;
  estimatedTime?: string;
  status: OrderStatus;
}

/**
 * Interfaz para resultado de validación de QR
 */
export interface QrValidationResult {
  success: boolean;
  orderId?: string;
  orderDetails?: QueuedOrder;
  message: string;
  timestamp: string;
}

/**
 * Interfaz para datos del QR
 */
interface QrData {
  orderId: string;
  timestamp: number;
  total: number;
  itemsCount: number;
  user: {
    id: string;
    name: string;
  };
  items: Array<{
    id: string;
    name: string;
    quantity: number;
    price: number;
  }>;
}

/**
 * Servicio para manejo de QR y estados de pedidos
 */
class QrService {
  private static readonly STORAGE_KEY = "chilenito-qr-codes";
  private static readonly ORDERS_STATUS_KEY = "chilenito-orders-status";

  /**
   * Valida un QR y pone el pedido en cola
   * @param qrCodePayload - Payload del código QR
   * @returns Promise con resultado de validación
   */
  static async validateAndQueueOrder(
    qrCodePayload: string
  ): Promise<QrValidationResult> {
    try {
      // Simular delay de procesamiento
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Decodificar el payload del QR (Base64 -> JSON)
      console.log("🔍 QR Payload recibido:", qrCodePayload);
      
      let qrData: QrData;
      try {
        // Intentar decodificar como Base64 primero
        const decodedPayload = atob(qrCodePayload);
        console.log("📄 Payload decodificado (Base64):", decodedPayload);
        qrData = JSON.parse(decodedPayload);
        console.log("✅ QR Data parseado:", qrData);
      } catch (base64Error) {
        console.log("⚠️ Error decodificando Base64:", base64Error);
        // Si falla Base64, intentar como JSON directo
        try {
          qrData = JSON.parse(qrCodePayload);
          console.log("✅ QR Data parseado (JSON directo):", qrData);
        } catch (jsonError) {
          console.error("❌ Error parseando JSON:", jsonError);
          throw new Error(`Formato de QR inválido: ${jsonError instanceof Error ? jsonError.message : 'Error desconocido'}`);
        }
      }

      // Verificar que el QR no haya sido usado antes
      const usedQrCodes = this.getUsedQrCodes();
      if (usedQrCodes.includes(qrData.orderId)) {
        return {
          success: false,
          message: "Este código QR ya ha sido canjeado",
          timestamp: new Date().toISOString(),
        };
      }

      // Obtener el pedido original
      console.log("🔍 Buscando orden con ID:", qrData.orderId);
      const order = await OrderService.getOrderById(qrData.orderId);
      console.log("📦 Orden encontrada:", order ? "SÍ" : "NO", order);
      
      if (!order) {
        console.error("❌ Orden no encontrada en la base de datos");
        return {
          success: false,
          message: "Pedido no encontrado",
          timestamp: new Date().toISOString(),
        };
      }

      // Verificar que el pedido esté pagado
      if (order.status !== "paid") {
        return {
          success: false,
          message: "El pedido no está pagado",
          timestamp: new Date().toISOString(),
        };
      }

      // NO marcar QR como usado aquí - solo cuando se complete la entrega
      // Cambiar estado del pedido a "En Preparación"
      await this.updateOrderStatus(qrData.orderId, "queued");

      // Crear objeto de pedido en cola
      const queuedOrder: QueuedOrder = {
        orderId: qrData.orderId,
        customerName: qrData.user?.name || (order.user ? `${order.user.nombres} ${order.user.apellidos}` : "Cliente Anónimo"),
        items: qrData.items || order.items || [],
        total: qrData.total || order.total || 0,
        queuedAt: new Date().toISOString(),
        status: "queued",
      };

      return {
        success: true,
        orderId: qrData.orderId,
        orderDetails: queuedOrder,
        message: `Pedido #${qrData.orderId} agregado a la cola de preparación`,
        timestamp: new Date().toISOString(),
      };
    } catch (error) {
      console.error("Error validando QR:", error);
      return {
        success: false,
        message: "Error al procesar el código QR",
        timestamp: new Date().toISOString(),
      };
    }
  }

  /**
   * Obtiene los pedidos en cola (estado "queued" o "preparing")
   * @returns Promise con lista de pedidos en cola
   */
  static async getQueuedOrders(): Promise<QueuedOrder[]> {
    try {
      // Simular delay de red
      await new Promise((resolve) => setTimeout(resolve, 300));

      const ordersStatus = this.getOrdersStatus();
      const queuedOrderIds = Object.keys(ordersStatus).filter((orderId) =>
        ["queued", "preparing", "ready"].includes(ordersStatus[orderId])
      );

      const queuedOrders: QueuedOrder[] = [];

      for (const orderId of queuedOrderIds) {
        const order = await OrderService.getOrderById(orderId);
        if (order) {
          queuedOrders.push({
            orderId: order.orderId,
            customerName: `${order.user.nombres} ${order.user.apellidos}`,
            items: order.items,
            total: order.total,
            queuedAt: order.orderDate,
            status: ordersStatus[orderId] as OrderStatus,
          });
        }
      }

      // Ordenar por tiempo de llegada (más antiguos primero)
      return queuedOrders.sort(
        (a, b) =>
          new Date(a.queuedAt).getTime() - new Date(b.queuedAt).getTime()
      );
    } catch (error) {
      console.error("Error obteniendo pedidos en cola:", error);
      return [];
    }
  }

  /**
   * Marca un pedido como listo para retirar
   * @param orderId - ID del pedido
   * @returns Promise con resultado
   */
  static async markOrderAsReady(orderId: string): Promise<boolean> {
    try {
      // Simular delay de procesamiento
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Verificar que el pedido existe y está en cola
      const order = await OrderService.getOrderById(orderId);
      if (!order) {
        throw new Error("Pedido no encontrado");
      }

      // Cambiar estado a listo
      await this.updateOrderStatus(orderId, "ready");

      return true;
    } catch (error) {
      console.error("Error marcando pedido como listo:", error);
      return false;
    }
  }

  /**
   * Marca un pedido como completado
   * @param orderId - ID del pedido
   * @returns Promise con resultado
   */
  static async completeOrder(orderId: string): Promise<boolean> {
    try {
      // Simular delay de procesamiento
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Verificar que el pedido existe y está en cola
      const order = await OrderService.getOrderById(orderId);
      if (!order) {
        throw new Error("Pedido no encontrado");
      }

      // Cambiar estado a completado
      await this.updateOrderStatus(orderId, "completed");

      return true;
    } catch (error) {
      console.error("Error completando pedido:", error);
      return false;
    }
  }

  /**
   * Actualiza el estado de un pedido
   * @param orderId - ID del pedido
   * @param status - Nuevo estado
   */
  private static async updateOrderStatus(
    orderId: string,
    status: OrderStatus
  ): Promise<void> {
    const ordersStatus = this.getOrdersStatus();
    ordersStatus[orderId] = status;
    localStorage.setItem(this.ORDERS_STATUS_KEY, JSON.stringify(ordersStatus));
  }

  /**
   * Obtiene los códigos QR ya utilizados
   * @returns Array de orderIds de QR usados
   */
  private static getUsedQrCodes(): string[] {
    const stored = localStorage.getItem(this.STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  }

  /**
   * Marca un QR como usado para evitar reutilización
   * @param orderId - ID del pedido
   */
  private static markQrAsUsed(orderId: string): void {
    const usedQrCodes = this.getUsedQrCodes();
    if (!usedQrCodes.includes(orderId)) {
      usedQrCodes.push(orderId);
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(usedQrCodes));
    }
  }

  /**
   * Marca un QR como completado/entregado (público para usar desde ScanPage)
   * @param orderId - ID del pedido
   */
  static markQrAsCompleted(orderId: string): void {
    this.markQrAsUsed(orderId);
  }

  /**
   * Remueve un QR de la lista de usados (para cuando se cancela sin completar)
   * @param orderId - ID del pedido
   */
  static removeQrFromUsed(orderId: string): void {
    const usedQrCodes = this.getUsedQrCodes();
    const index = usedQrCodes.indexOf(orderId);
    if (index > -1) {
      usedQrCodes.splice(index, 1);
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(usedQrCodes));
    }
  }

  /**
   * Obtiene el estado de todos los pedidos
   * @returns Objeto con orderId como clave y estado como valor
   */
  private static getOrdersStatus(): Record<string, OrderStatus> {
    const stored = localStorage.getItem(this.ORDERS_STATUS_KEY);
    return stored ? JSON.parse(stored) : {};
  }

  /**
   * Obtiene el estado de un pedido específico
   * @param orderId - ID del pedido
   * @returns Estado del pedido o 'paid' por defecto
   */
  static getOrderStatus(orderId: string): OrderStatus {
    const ordersStatus = this.getOrdersStatus();
    return ordersStatus[orderId] || "paid";
  }

  /**
   * Limpia todos los datos de QR (para testing)
   */
  static clearAllData(): void {
    localStorage.removeItem(this.STORAGE_KEY);
    localStorage.removeItem(this.ORDERS_STATUS_KEY);
  }
}

export default QrService;
